// src/pages/customer-panel/feedback/FeedbackPage.js
// Feedback List Page — matches GET /chat/feedback/all response

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    InputAdornment,
    Tooltip,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const ISSUE_COLORS = {
    'Incorrect information': { color: '#ef4444', bg: '#fef2f2' },
    'Offensive content': { color: '#dc2626', bg: '#fef2f2' },
    'Not helpful': { color: '#f59e0b', bg: '#fef3c7' },
    'Outdated information': { color: '#f97316', bg: '#fff7ed' },
    'Other': { color: '#64748b', bg: '#f1f5f9' },
};

export default function FeedbackPage() {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || 'customer';
    const isAdmin = userRole === 'admin';

    const [feedbackList, setFeedbackList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [contextModal, setContextModal] = useState(null);

    const fetchFeedback = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                page: page + 1,
                limit: rowsPerPage,
            });
            if (search.trim()) params.append('search', search.trim());
            const response = await axiosInstance.get(`/chat/feedback/all?${params.toString()}`);
            const data = response.data;
            setFeedbackList(data.data || []);
            setTotalCount(data.total || 0);
        } catch (err) {
            console.error('Failed to fetch feedback:', err);
            setError(err.response?.data?.message || 'Failed to load feedback.');
        } finally {
            setLoading(false);
        }
    }, [search, page, rowsPerPage]);

    useEffect(() => {
        document.title = 'Feedback | Customer Panel';
        fetchFeedback();
    }, [fetchFeedback]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '--';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        } catch {
            return '--';
        }
    };

    const getIssueChip = (issue) => {
        const config = ISSUE_COLORS[issue] || ISSUE_COLORS['Other'];
        return (
            <Chip
                label={issue || 'Unknown'}
                size="small"
                sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: config.bg, color: config.color }}
            />
        );
    };

    if (!isAdmin) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Alert severity="warning" sx={{ fontFamily, borderRadius: '12px' }}>
                    <Typography sx={{ fontFamily, fontWeight: 600 }}>Access Denied</Typography>
                    <Typography variant="body2" sx={{ fontFamily }}>Only administrators can view feedback.</Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2, px: 3, mb: 3, borderRadius: '16px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FeedbackIcon sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>Chatbot Feedback</Typography>
                            <Typography variant="body2" sx={{ fontFamily, opacity: 0.9 }}>View user feedback on chatbot responses</Typography>
                        </Box>
                    </Box>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchFeedback} sx={{ color: '#fff' }}><RefreshIcon /></IconButton>
                    </Tooltip>
                </Box>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>{error}</Alert>}

            <Paper sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'grey.100', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                {/* Search */}
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <TextField
                        placeholder="Search feedback, questions, or responses..."
                        size="small"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        sx={{ flex: 1, minWidth: 200, maxWidth: 500, '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }}
                    />
                    <Typography variant="body2" sx={{ fontFamily, color: '#94a3b8' }}>
                        {totalCount} result{totalCount !== 1 ? 's' : ''}
                    </Typography>
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                {['User', 'Issue', 'Feedback', 'Context', 'Date'].map((header) => (
                                    <TableCell
                                        key={header}
                                        sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                    >
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                                    </TableCell>
                                </TableRow>
                            ) : feedbackList.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                                        <FeedbackIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No feedback found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                feedbackList.map((fb) => (
                                    <TableRow key={fb.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        {/* User */}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: GREEN_ACCENT, fontSize: '0.8rem', fontWeight: 600 }}>
                                                    {(fb.user?.name || fb.user?.email || '?').charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                        {fb.user?.name || 'Unknown'}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>
                                                        {fb.user?.email || ''}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        {/* Issue */}
                                        <TableCell>{getIssueChip(fb.issue)}</TableCell>
                                        {/* Feedback */}
                                        <TableCell sx={{ maxWidth: 300 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}
                                            >
                                                {fb.feedback || '--'}
                                            </Typography>
                                        </TableCell>
                                        {/* Context */}
                                        <TableCell>
                                            {(fb.user_question || fb.ai_response) ? (
                                                <Tooltip title="View conversation context">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setContextModal(fb)}
                                                        sx={{ color: GREEN_ACCENT }}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" sx={{ fontFamily, color: '#cbd5e1' }}>--</Typography>
                                            )}
                                        </TableCell>
                                        {/* Date */}
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                            {formatDate(fb.created_date)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{ '& *': { fontFamily } }}
                />
            </Paper>

            {/* Conversation Context Modal */}
            <Dialog
                open={!!contextModal}
                onClose={() => setContextModal(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    Conversation Context
                    <IconButton onClick={() => setContextModal(null)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    {contextModal && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Issue & Feedback */}
                            <Box sx={{ p: 2, bgcolor: '#fef3c7', borderRadius: '12px', border: '1px solid #fde68a' }}>
                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: '#92400e', mb: 0.5 }}>
                                    Issue: {contextModal.issue}
                                </Typography>
                                {contextModal.feedback && (
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#78350f' }}>
                                        {contextModal.feedback}
                                    </Typography>
                                )}
                            </Box>

                            {/* User Question */}
                            {contextModal.user_question && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5, px: 2, maxWidth: '80%',
                                            borderRadius: '14px 14px 4px 14px',
                                            bgcolor: GREEN_ACCENT, color: '#fff',
                                            '& p': { margin: 0, fontFamily, fontSize: '0.85rem', lineHeight: 1.5 },
                                        }}
                                    >
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>{contextModal.user_question}</Typography>
                                    </Paper>
                                    <Avatar sx={{ bgcolor: '#6366f1', width: 28, height: 28, mt: 0.5 }}>
                                        <PersonIcon sx={{ fontSize: 14 }} />
                                    </Avatar>
                                </Box>
                            )}

                            {/* AI Response */}
                            {contextModal.ai_response && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                                    <Avatar sx={{ bgcolor: GREEN_ACCENT, width: 28, height: 28, mt: 0.5 }}>
                                        <SmartToyIcon sx={{ fontSize: 14 }} />
                                    </Avatar>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5, px: 2, maxWidth: '80%',
                                            borderRadius: '14px 14px 14px 4px',
                                            bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
                                            '& p': { margin: 0, fontFamily, fontSize: '0.85rem', lineHeight: 1.5 },
                                            '& a': { color: '#2563eb', textDecoration: 'underline' },
                                        }}
                                    >
                                        <ReactMarkdown
                                            rehypePlugins={[rehypeRaw]}
                                            components={{ a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" /> }}
                                        >
                                            {contextModal.ai_response}
                                        </ReactMarkdown>
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
